import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { fetchWorkHours, failureCode, type WorkHoursView } from "../services/ccTenantViewService";
import { Card, Kpi, PageHeader, StateBox, hours } from "../components/ui";

// Horas Trabajadas vistas desde la app del cliente (CC-CONSOLE #978 / #981):
// solo lo que Coding Cloud trabajó para ESTE cliente. Solo lectura.

const RANGES = [30, 90, 180] as const;
const isoDaysAgo = (days: number) => new Date(Date.now() - 6 * 3600 * 1000 - days * 86400000).toISOString().slice(0, 10);

export default function WorkHoursClientView() {
  const { t } = useTranslation("common");
  const [days, setDays] = useState<(typeof RANGES)[number]>(30);
  const [view, setView] = useState<WorkHoursView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Solo la última petición pinta: cambiar de rango rápido no deja datos viejos.
  const requestId = useRef(0);
  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const next = await fetchWorkHours(isoDaysAgo(days - 1), isoDaysAgo(0));
      if (id === requestId.current) setView(next);
    } catch (err) {
      if (id !== requestId.current) return;
      setView(null);
      setError(failureCode(err) ?? "generic");
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    void load();
  }, [load]);

  const s = view?.summary;
  return (
    <div className="w-full max-w-6xl space-y-4 overflow-y-auto p-4 sm:p-6">
      <PageHeader title={t("clientView.workHours.title")} subtitle={t("clientView.workHours.subtitle", { client: view?.client.name ?? "" })}>
        {RANGES.map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => setDays(r)}
            className={`rounded-lg px-3 py-2 text-xs font-medium ${days === r ? "bg-primary-500 text-on-primary" : "border border-line text-content-secondary hover:bg-subtle"}`}
          >
            {t("clientView.lastDays", { count: r })}
          </button>
        ))}
      </PageHeader>

      <StateBox loading={loading} error={error} onRetry={() => void load()} />

      {view && s && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Kpi
              label={t("clientView.workHours.total")}
              value={hours(s.totalHours)}
              hint={view.estimated ? t("clientView.workHours.estimatedHint", { recorded: hours(s.recordedHours), estimated: hours(s.estimatedHours) }) : undefined}
            />
            <Kpi label={t("clientView.workHours.people")} value={view.shareTeam ? String(s.activeUsers) : "—"} />
            <Kpi label={t("clientView.workHours.commits")} value={String(s.totalCommits)} />
            <Kpi
              label={t("clientView.workHours.expected")}
              value={view.expected ? hours(view.expected.teamExpectedHours) : "—"}
              hint={view.expected ? t("clientView.workHours.gap", { gap: hours(view.expected.gapHours) }) : undefined}
            />
          </div>

          <Card title={t("clientView.workHours.weekly")}>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={s.weekly.map((w) => ({ label: w.weekStart.slice(5), hours: w.hours }))}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                  <XAxis dataKey="label" fontSize={11} />
                  <YAxis fontSize={11} />
                  <Tooltip formatter={(v: number) => hours(v)} />
                  <Bar dataKey="hours" fill="#7c3aed" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card title={t("clientView.workHours.byProject")}>
              <Rows rows={(s.byProject ?? []).map((p) => [p.projectLabel, hours(p.hours)])} empty={t("clientView.empty")} />
            </Card>
            <Card title={t("clientView.workHours.byRepo")}>
              <Rows rows={s.byRepo.map((r) => [r.repo, hours(r.hours)])} empty={t("clientView.empty")} />
            </Card>
            {view.shareTeam && (
              <Card title={t("clientView.workHours.byPerson")}>
                <Rows rows={s.byUser.map((u) => [u.userName ?? "—", hours(u.hours)])} empty={t("clientView.empty")} />
              </Card>
            )}
            <Card title={t("clientView.workHours.pullRequests")}>
              <Rows
                rows={s.pullRequests.slice(0, 20).map((p) => [
                  p.url ? (
                    <a key={p.key} href={p.url} target="_blank" rel="noopener noreferrer" className="text-primary-500 hover:underline">
                      {p.title ?? `${p.repo}#${p.number}`}
                    </a>
                  ) : (
                    p.title ?? `${p.repo}#${p.number}`
                  ),
                  hours(p.hours),
                ])}
                empty={t("clientView.empty")}
              />
            </Card>
          </div>
          {!view.shareTeam && <p className="text-xs text-content-secondary">{t("clientView.anonymized")}</p>}
        </>
      )}
    </div>
  );
}

function Rows({ rows, empty }: { rows: [React.ReactNode, string][]; empty: string }) {
  if (!rows.length) return <p className="text-xs text-content-secondary">{empty}</p>;
  return (
    <table className="w-full text-sm">
      <tbody>
        {rows.map(([label, value], i) => (
          <tr key={i} className="border-t border-line text-content first:border-t-0">
            <td className="py-1.5 pr-2">{label}</td>
            <td className="py-1.5 text-right tabular-nums">{value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
